import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import PortalLayout from "@/components/ventrix/PortalLayout";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Trash2, Pencil, Truck } from "lucide-react";
import { ModeIcon } from "@/components/ventrix/atoms";
import Pagination from "@/components/ventrix/Pagination";
import { useAuth, isAdmin } from "@/context/AuthContext";

const MODES = ["air", "sea", "road"];
const LIMIT = 50;
const blank = () => ({ name: "", carrier_type: "partner", modes: [], code: "", country: "", active: true });

export default function Carriers() {
  const { user } = useAuth();
  const [items, setItems] = useState(null);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [f, setF] = useState(blank());
  const canDelete = isAdmin(user?.role);

  const load = () => {
    setItems(null);
    api.get(`/v1/carriers?limit=${LIMIT}&offset=${offset}`)
      .then(({ data }) => { setItems(data.items || []); setTotal(data.total ?? (data.items || []).length); })
      .catch(() => setItems([]));
  };
  useEffect(() => { load(); }, [offset]); // eslint-disable-line

  const startCreate = () => { setEditing(null); setF(blank()); setOpen(true); };
  const startEdit = (c) => { setEditing(c); setF({ ...blank(), ...c }); setOpen(true); };
  const toggleMode = (m) => setF((p) => ({ ...p, modes: p.modes.includes(m) ? p.modes.filter((x) => x !== m) : [...p.modes, m] }));

  const save = async () => {
    try {
      if (!f.name.trim()) { toast.error("Name is required"); return; }
      const payload = { ...f, code: f.code || null, country: f.country || null };
      if (editing) await api.patch(`/v1/carriers/${editing.id}`, payload);
      else await api.post("/v1/carriers", payload);
      setOpen(false); load(); toast.success("Carrier saved");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const del = async (c) => {
    if (!window.confirm("Delete this carrier?")) return;
    try { await api.delete(`/v1/carriers/${c.id}`); load(); toast.success("Deleted"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <PortalLayout section="ops">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-6">
        <div>
          <div className="vtx-eyebrow">/ Ops · Carriers</div>
          <h1 className="mt-2 text-3xl font-black text-[#0E2F76] tracking-tighter">Carrier registry</h1>
        </div>
        <button className="vtx-btn-primary inline-flex items-center gap-2" onClick={startCreate} data-testid="new-carrier"><Plus size={14} /> New carrier</button>
      </div>

      <div className="vtx-card">
        {items === null && <div className="p-6 space-y-2">{[1, 2, 3].map((i) => <div key={i} className="vtx-skeleton h-12" />)}</div>}
        {items && (
          <div className="overflow-x-auto">
            <table className="vtx-table w-full">
              <thead><tr><th>Carrier</th><th>Type</th><th>Modes</th><th>Code</th><th>Country</th><th>Active</th><th></th></tr></thead>
              <tbody>
                {items.length === 0 && <tr><td colSpan="7" className="text-center text-sm text-[#475569] py-10">No carriers yet.</td></tr>}
                {items.map((c) => (
                  <tr key={c.id} data-testid={`carrier-row-${c.id}`}>
                    <td className="font-semibold inline-flex items-center gap-2"><Truck size={13} className="text-[#0E2F76]" /> {c.name}</td>
                    <td><span className={`text-[10px] uppercase tracking-wider font-bold ${c.carrier_type === "self" ? "text-[#0E2F76]" : "text-[#475569]"}`}>{c.carrier_type}</span></td>
                    <td className="flex gap-1.5 py-2">{(c.modes || []).map((m) => <ModeIcon key={m} mode={m} size={13} />)}</td>
                    <td className="font-mono text-xs">{c.code || "—"}</td>
                    <td className="text-xs">{c.country || "—"}</td>
                    <td>{c.active ? <span className="text-[#166534] font-bold text-xs">ACTIVE</span> : <span className="text-[#475569] text-xs">Off</span>}</td>
                    <td className="flex gap-2">
                      <button onClick={() => startEdit(c)} data-testid={`edit-carrier-${c.id}`} className="text-[#0E2F76] hover:text-[#d41f26]"><Pencil size={14} /></button>
                      {canDelete && <button onClick={() => del(c)} data-testid={`del-carrier-${c.id}`} className="text-[#d41f26]"><Trash2 size={14} /></button>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <Pagination total={total} limit={LIMIT} offset={offset} onChange={setOffset} />
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white max-w-lg">
          <DialogHeader><DialogTitle>{editing ? "Edit" : "New"} carrier</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <label className="col-span-2"><span className="vtx-eyebrow block mb-1.5">Name</span><input className="vtx-input" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="carrier-name" /></label>
            <label><span className="vtx-eyebrow block mb-1.5">Type</span>
              <select className="vtx-input" value={f.carrier_type} onChange={(e) => setF({ ...f, carrier_type: e.target.value })} data-testid="carrier-type">
                <option value="partner">Partner</option><option value="self">Self (Ventrix)</option>
              </select>
            </label>
            <label><span className="vtx-eyebrow block mb-1.5">Code (SCAC/IATA)</span><input className="vtx-input" value={f.code} onChange={(e) => setF({ ...f, code: e.target.value })} /></label>
            <label><span className="vtx-eyebrow block mb-1.5">Country</span><input className="vtx-input" value={f.country} onChange={(e) => setF({ ...f, country: e.target.value })} /></label>
            <div className="col-span-2">
              <span className="vtx-eyebrow block mb-1.5">Modes</span>
              <div className="flex gap-2">
                {MODES.map((m) => (
                  <button type="button" key={m} onClick={() => toggleMode(m)}
                    className={`px-3 py-1.5 border text-xs font-bold uppercase tracking-wider ${f.modes.includes(m) ? "bg-[#0E2F76] text-white border-[#0E2F76]" : "bg-white text-[#0E2F76] border-[#A9C0E0]/60"}`}
                    style={{ borderRadius: 2 }} data-testid={`carrier-mode-${m}`}>{m}</button>
                ))}
              </div>
            </div>
            <label className="flex items-center gap-2 mt-2"><input type="checkbox" checked={f.active} onChange={(e) => setF({ ...f, active: e.target.checked })} /> <span className="text-sm font-semibold">Active</span></label>
          </div>
          <DialogFooter>
            <button className="vtx-btn-ghost" onClick={() => setOpen(false)}>Cancel</button>
            <button className="vtx-btn-primary" onClick={save} data-testid="save-carrier">Save</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PortalLayout>
  );
}
